/**
 * Revelar uma credencial: pedir o motivo, ver o valor, e vê-lo sumir.
 *
 * É o único componente do portal que chega a ter uma senha de cliente na mão, e o desenho inteiro
 * é sobre onde ela **não** fica:
 *
 * · **Fora do react-query.** `revelarSegredo` é chamada direta, não `useMutation`. O
 *   `MutationCache` guarda `variables` e `data` de cada mutação e sobrevive à desmontagem do
 *   componente até o `gcTime` — usar o hook deixaria a senha viva num cache global do navegador
 *   por minutos depois de a tela ter sumido. Nada aqui chama `setQueryData`.
 *
 * · **Fora de `localStorage`, `sessionStorage` e da URL.** Não há nenhuma escrita em storage neste
 *   arquivo, nem parâmetro de rota que carregue valor. O único endereço envolvido é o `POST` da
 *   revelação, e o que vai nele é o motivo.
 *
 * · **Numa `useState` de vida curta, e só nela.** O valor não sobe para o componente pai, não vira
 *   `prop` de ninguém, não entra em atributo `data-*`, `title` ou `key`, e não é escrito num
 *   `<input>` — um `<input>` faz o gerenciador de senhas do navegador oferecer para guardar a
 *   credencial do cliente no cofre pessoal de quem revelou. Sai por três caminhos: o botão
 *   "Ocultar", o relógio de {@link SEGUNDOS_NA_TELA} segundos e a desmontagem da tela.
 *
 * O que este componente **não** promete: apagar a string da memória do processo. JavaScript não
 * dá apagamento determinístico de `string` — trocar o estado por `null` remove a referência e
 * deixa o resto com o coletor de lixo, e escrever aqui que "a senha foi apagada" seria a mesma
 * classe de mentira que o cofre existe para não contar.
 *
 * A autorização também não mora aqui. Quem não tem `credencial:revelar` não recebe este
 * componente na tela — mas isso é cortesia, não defesa: a negativa acontece no serviço, que grava
 * a linha de `segredo_revelacao` e é onde o teste negativo roda.
 */

import { useEffect, useRef, useState } from "react";
import { Aviso, Botao, CampoLongo } from "../ui";
import {
  MOTIVO_MAXIMO,
  MOTIVO_MINIMO,
  formatarInstante,
  revelarSegredo,
  useEscritaDoCofre,
  type ValorRevelado,
} from "../../nucleo/cofre";

/**
 * Quanto tempo o valor fica na tela antes de sumir sozinho.
 *
 * Longo o bastante para copiar e colar num terminal, curto o bastante para que a aba esquecida
 * aberta na copa não seja uma credencial de cliente exposta. Quem precisa de novo revela de novo
 * — e a segunda revelação também vira linha, que é o ponto.
 */
export const SEGUNDOS_NA_TELA = 90;

export interface PropsDaRevelacao {
  segredoId: string;
  /** Como a credencial é chamada — para a pessoa saber o que está prestes a ver. */
  rotulo: string;
  /** Trava o botão enquanto outra escrita da mesma tela está no ar. */
  desabilitado?: boolean;
  /**
   * Chamado depois de uma revelação bem-sucedida.
   *
   * Serve para o pai reler a listagem: o contador de revelações sobe **na frente de quem acabou
   * de revelar**. Registro que quem usa não vê acontecer é registro que ele esquece que existe.
   */
  aoRevelar?: () => void;
}

export function RevelacaoDoSegredo({
  segredoId,
  rotulo,
  desabilitado = false,
  aoRevelar,
}: PropsDaRevelacao) {
  const [pedindo, setPedindo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [revelado, setRevelado] = useState<ValorRevelado | null>(null);
  const [restante, setRestante] = useState(SEGUNDOS_NA_TELA);
  const escrita = useEscritaDoCofre();
  const campoDoMotivo = useRef<HTMLTextAreaElement>(null);

  /* O relógio que apaga o valor. Ele existe enquanto houver valor na tela e some junto com ele:
     sem a dependência em `revelado`, um intervalo órfão continuaria contando para nada.

     A conta é feita contra o RELÓGIO DE PAREDE, e não somando ticks. Navegador engasga timer de
     aba em segundo plano — descontando um por tick, a aba minimizada com a senha na tela levaria
     muito mais que os 90 segundos combinados para apagá-la, que é justamente o caso em que a
     credencial fica exposta sem ninguém olhando. */
  useEffect(() => {
    if (!revelado) return;
    const limite = Date.now() + SEGUNDOS_NA_TELA * 1000;
    const relogio = setInterval(() => {
      const segundos = Math.ceil((limite - Date.now()) / 1000);
      if (segundos <= 0) {
        setRevelado(null);
        setRestante(SEGUNDOS_NA_TELA);
        return;
      }
      setRestante(segundos);
    }, 250);
    return () => clearInterval(relogio);
  }, [revelado]);

  const motivoLimpo = motivo.trim();
  const motivoCurto = motivoLimpo.length < MOTIVO_MINIMO;

  function abrirPedido() {
    escrita.limparErro();
    setPedindo(true);
    // O foco vai para o motivo: o campo é a razão de a caixa ter aberto, e quem usa teclado não
    // deveria ter de procurá-lo.
    queueMicrotask(() => campoDoMotivo.current?.focus());
  }

  function fecharPedido() {
    setPedindo(false);
    setMotivo("");
    escrita.limparErro();
  }

  async function confirmar() {
    if (motivoCurto) return;
    const feito = await escrita.executar(async () => {
      const resposta = await revelarSegredo(segredoId, motivoLimpo);
      setRevelado(resposta);
    });
    if (feito) {
      setPedindo(false);
      // O motivo sai do estado junto: ele não é segredo, mas também não tem por que sobreviver
      // ao ato que o justificou — e deixá-lo convidaria a próxima revelação a reusá-lo sem pensar.
      setMotivo("");
      aoRevelar?.();
    }
  }

  if (revelado) {
    return (
      <ValorEmClaro
        revelado={revelado}
        restante={restante}
        aoOcultar={() => setRevelado(null)}
      />
    );
  }

  if (!pedindo) {
    return (
      <Botao
        variante="fantasma"
        tamanho="pequeno"
        disabled={desabilitado}
        data-testid="revelar-credencial"
        onClick={abrirPedido}
      >
        Revelar valor
      </Botao>
    );
  }

  return (
    <div className="revelacao" data-testid="pedido-de-revelacao">
      <p className="revelacao__aviso">
        <b>Ver “{rotulo}” fica registrado.</b> A revelação vira uma linha com o seu nome, a data,
        o IP e este motivo — e a linha não pode ser apagada nem editada depois.
      </p>
      <CampoLongo
        ref={campoDoMotivo}
        rotulo="Por que você precisa ver esta credencial?"
        obrigatorio
        rows={2}
        maxLength={MOTIVO_MAXIMO}
        value={motivo}
        ajuda={
          `Pelo menos ${MOTIVO_MINIMO} caracteres. ` +
          "Escreva para quem for ler a auditoria daqui a seis meses."
        }
        {...(escrita.erro ? { erro: escrita.erro } : {})}
        onChange={(evento) => setMotivo(evento.target.value)}
        data-testid="motivo-da-revelacao"
      />
      <div className="revelacao__acoes">
        <Botao variante="fantasma" tamanho="pequeno" onClick={fecharPedido}>
          Cancelar
        </Botao>
        <Botao
          tamanho="pequeno"
          disabled={escrita.executando || motivoCurto}
          data-testid="confirmar-revelacao"
          onClick={() => void confirmar()}
        >
          {escrita.executando ? "Revelando…" : "Revelar e registrar"}
        </Botao>
      </div>
    </div>
  );
}

/** O valor na tela, com o relógio correndo. Não existe caminho daqui para fora do componente. */
function ValorEmClaro({
  revelado,
  restante,
  aoOcultar,
}: {
  revelado: ValorRevelado;
  restante: number;
  aoOcultar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);
  /* `navigator.clipboard` não existe fora de contexto seguro. Oferecer um botão que não faz nada
     é pior que não oferecer: a pessoa clica, acha que copiou e cola outra coisa. */
  const podeCopiar =
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.writeText === "function";

  async function copiar() {
    /* A área de transferência é um canal que a revelação NÃO registra: depois daqui o valor sai
       do alcance do portal. O botão existe mesmo assim porque a alternativa é a pessoa
       selecionar com o mouse e copiar do mesmo jeito — recusar o botão não fecha o canal, só o
       torna desajeitado. */
    try {
      await navigator.clipboard.writeText(revelado.valor);
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  }

  return (
    <div className="revelacao revelacao--aberta" data-testid="revelacao-aberta">
      {/* A região viva anuncia que o valor apareceu e que ficou registrado — e NÃO contém o
          valor: um leitor de tela lendo a senha do banco do ERP em voz alta num escritório
          aberto seria o vazamento que esta tela existe para não ter. */}
      <p className="revelacao__cabecalho" role="status">
        <b>{revelado.rotulo}</b> — revelada em{" "}
        {formatarInstante(revelado.ocorrido_em) ?? "agora"}. O acesso ficou registrado com o seu
        nome, a hora e o IP.
      </p>

      {/* `<code>`, e não `<input>`: campo de formulário faz o gerenciador de senhas do navegador
          oferecer para guardar a credencial do CLIENTE no cofre pessoal de quem revelou. */}
      <code className="revelacao__valor" data-testid="valor-revelado">
        {revelado.valor}
      </code>

      {revelado.sob_custodia_provisoria ? (
        <Aviso tom="atencao" className="mt-3" data-testid="revelado-sob-provisoria">
          <b>Esta credencial está com a proteção provisória.</b> Até a chave definitiva ser
          ativada, quem comprometesse o servidor chegaria a ela sem passar por este registro.
          Ela será protegida de novo quando a chave definitiva existir.
        </Aviso>
      ) : null}

      <div className="revelacao__acoes">
        <p className="revelacao__relogio" data-testid="relogio-da-revelacao">
          Some da tela em <span className="numero-tabular">{restante}s</span>
        </p>
        {podeCopiar ? (
          <Botao variante="fantasma" tamanho="pequeno" onClick={() => void copiar()}>
            {copiado ? "Copiado" : "Copiar"}
          </Botao>
        ) : null}
        <Botao tamanho="pequeno" data-testid="ocultar-valor" onClick={aoOcultar}>
          Ocultar agora
        </Botao>
      </div>
    </div>
  );
}
